import { Item } from "@dto/records";
import { RefObject } from "react";
import { CellPosition } from "./GridCell";


type AbstractCellProps = {
    item: Item;
    position: CellPosition | null;
    updateValue: (item: Item, value: any) => void;
    isEditing: boolean;
    textToValue: (text: string | null) => any;
    isTextValid: (text: string | null) => boolean;
    text: string | null;
    setText: (text: string | null) => void;
    inputRef: RefObject<HTMLInputElement>;
    readOnly: boolean;
    renderReadOnlyContent: () => React.ReactNode;
    renderEditingContent: (
        onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
        onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void,
        inputRef: RefObject<HTMLInputElement>
    ) => React.ReactNode;
}

export function AbstractCell({ item, updateValue, renderReadOnlyContent, renderEditingContent, textToValue, isTextValid, text, setText, isEditing, inputRef, readOnly }: AbstractCellProps) {

    // console.log(" RenderingAbstractCell", text);

    function validate() {

        // if the text is not valid, do nothing
        if (!isTextValid(text)) {
            return;
        }

        let value = textToValue(text);
        let itemValue = item?.value ? item.value : null;

        // if the value is the same as the item value, do nothing
        if (value === itemValue) {
            return;
        }

        const newItem = { ...item, value: value };
        updateValue(newItem, value);
    };

    function cancel() {
        // Normalize null/undefined to an empty string: cells expect `text` to be a string
        // (e.g. textToValue calls text.length), otherwise Escape on an empty cell crashes.
        const originalText = item?.value === null || item?.value === undefined ? "" : String(item.value);
        setText(originalText);
        if (inputRef.current) {
            inputRef.current.value = originalText;
        }
    }

    function onEnterKeyPressEditing(event: React.KeyboardEvent) {
        switch (event.key) {
            case "Enter":
                event.preventDefault();
                if (isEditing) {
                    validate();
                }
                break;
            case "Tab":
                // Propagation to GridCell will handle the validation (unfocus triggered)
                event.preventDefault();
                break;
            case "Escape":
                event.preventDefault();
                cancel();
                break;
            default:
                if (event.ctrlKey) {
                    // Stop propagation of ctrl+c, ctrl+v, ctrl+x so that the behavior of GridCell is not triggered
                    if (event.key === "c" || event.key === "v" || event.key === "x") {
                        event.stopPropagation();
                    }
                    return;
                }
                return;
        }
    }

    function onTextChange(event: React.ChangeEvent<HTMLInputElement>) {
        let value = event.target.value;
        if (isTextValid(value)) {
            setText(value);
        } else {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    return (
        <>
            {
                (!readOnly && (isEditing || !renderReadOnlyContent)) ?
                    renderEditingContent(onTextChange, onEnterKeyPressEditing, inputRef) :
                    renderReadOnlyContent()
            }
        </>
    )
}
